/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Box } from '@material-ui/core';
import clsx from 'clsx';
import {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import Tooltip from 'components/ChoreoSystem/Tooltip/Tooltip';
import { getApplicationIcon } from 'components/ChoreoSystem/Cards/ApplicationCard/ApplicationIcon';
import { ArrowRightLong } from 'components/ChoreoSystem/Icons/generated';
import useStyles, {
  MARQUEE_SHIFT_VAR,
} from './PrebuiltIntegrationFlows.styles';
import {
  usePrebuiltIntegrationFlows,
  PrebuiltIntegrationFlow,
} from './usePrebuiltIntegrationFlows';

// Speed rather than duration, because the length of a copy depends on how many
// flows the catalogue publishes: a fixed duration would silently change the
// pace whenever that list or the chip size did. Matches the connector marquee
// this row replaced, so the page drifts at the pace it always has.
/** Pixels a second the row drifts at. */
const ROW_SPEED_PX_S = 18;

/**
 * Backstop on the copy count. Nothing should ever reach it — an ultrawide
 * window over the narrowest plausible copy is well inside it — but a bad
 * measurement must degrade into a slightly gappy strip, never into a track
 * that grows without bound.
 */
const MAX_TRACK_COPIES = 12;

/**
 * How many times the row has to repeat its flows to never show a gap.
 *
 * The scroll consumes exactly one copy before the track resets, so the visible
 * window never reaches further than `containerWidth + copyWidth` into the
 * track; a track at least that wide is covered at every point of the
 * animation, the first frame included.
 *
 * `containerWidth` is the width of the *visible* strip, never the track's own
 * width. Measuring the track here would feed back on itself: the track is
 * `max-content`, so each added copy widens it, which asks for another copy.
 */
export const resolveCopyCount = (
  containerWidth: number,
  copyWidth: number
): number => {
  if (containerWidth <= 0 || copyWidth <= 0) return 1;
  return Math.min(Math.ceil(containerWidth / copyWidth) + 1, MAX_TRACK_COPIES);
};

/** Whether the visitor has asked for motion to be kept to a minimum. */
const usePrefersReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Absent in jsdom and on older browsers; there the row animates, which is
    // the behaviour without this hook.
    if (typeof window.matchMedia !== 'function') return undefined;

    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(query.matches);

    const onChange = (event: MediaQueryListEvent) =>
      setPrefersReducedMotion(event.matches);
    // `addListener` is the deprecated spelling, kept for Safari below 14.
    if (query.addEventListener) {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, []);

  return prefersReducedMotion;
};

interface PrebuiltIntegrationFlowsProps {
  /** Called with the picked flow; the landing page turns it into a sign up. */
  onIntegrationClick: (flow: PrebuiltIntegrationFlow) => void;
}

interface FlowRowProps {
  flows: PrebuiltIntegrationFlow[];
  onIntegrationClick: (flow: PrebuiltIntegrationFlow) => void;
  speedPxS: number;
  /**
   * Width of the visible strip, measured by the parent. Passed in rather than
   * measured here because the only element this component owns is the track,
   * whose width is a function of the copy count it would be feeding.
   */
  containerWidth: number;
  /**
   * Stops the marquee while the strip is out of view. Decided by the parent,
   * which owns the element the observer watches.
   */
  paused: boolean;
  /** Holds the row still and drops it to a single copy. */
  isStatic: boolean;
}

function FlowRowBase(props: FlowRowProps) {
  const {
    flows,
    onIntegrationClick,
    speedPxS,
    containerWidth,
    paused,
    isStatic,
  } = props;
  const classes = useStyles();
  const intl = useIntl();
  const copyRef = useRef<HTMLDivElement>(null);
  const [copyWidth, setCopyWidth] = useState(0);

  // ── One copy's width ──────────────────────────────────────────────────
  // A single copy's width is its own content's — the whole flow list — so it
  // does not move when copies are added around it. That is what makes it safe
  // to measure from in here.
  useEffect(() => {
    const copy = copyRef.current;
    if (!copy) return undefined;

    // Only writes state when the width actually moved: a ResizeObserver fires
    // on every frame of a window drag, and the row holds enough chips that a
    // re-render per frame is felt.
    const measure = () => {
      const next = copy.getBoundingClientRect().width;
      setCopyWidth((current) => (current === next ? current : next));
    };

    measure();

    // Absent in jsdom, and the initial measurement is all a test needs.
    if (typeof ResizeObserver === 'undefined') return undefined;

    // Catches the flow list or the theme's chip size changing under it.
    const observer = new ResizeObserver(measure);
    observer.observe(copy);
    return () => observer.disconnect();
  }, []);

  const isMeasured = copyWidth > 0 && containerWidth > 0;
  const copies = isStatic ? 1 : resolveCopyCount(containerWidth, copyWidth);
  // One cycle covers exactly one copy, so this holds the row to `speedPxS`
  // whatever that copy turns out to measure.
  //
  // Zero until the copy has been measured, which parks the row at rest for the
  // frame between first paint and the measurement — the shift is still `0px`
  // then, so there is nothing to animate anyway. The unmeasured row is shown
  // rather than hidden: the chips are buttons, and a measurement that never
  // lands must degrade into a strip that is short, not one nobody can press.
  const durationS = isMeasured ? copyWidth / speedPxS : 0;

  // Built once per flow list: the row carries tens of chips, and rebuilding
  // them on an unrelated render is the most expensive thing this component can
  // do.
  //
  // Two sets, not one. Only the first copy is a real set of controls; the
  // repeats exist to fill the track, and left as they are they would put the
  // same chips into the Tab order and into a screen reader's list over and
  // over. They stay clickable with the pointer — a visitor presses whichever
  // chip happens to be in front of them — but they are inert to everything
  // else.
  const buildTiles = useCallback(
    (isDuplicate: boolean) =>
      flows.map((flow) => {
        const [source, target] = flow.applications;
        const chip = (
          <button
            type="button"
            className={classes.tile}
            // The chip is two logos and an arrow, with nothing written on it.
            // This is the only thing that names the pair for anyone not
            // looking at the screen.
            aria-label={intl.formatMessage(
              {
                id: 'modules.login.prebuiltIntegrationFlows.flowLabel',
                defaultMessage: 'Integrate {source} with {target}',
              },
              { source, target }
            )}
            tabIndex={isDuplicate ? -1 : undefined}
            data-testid={
              isDuplicate ? undefined : `prebuilt-integration-flow-${flow.key}`
            }
            onClick={() => onIntegrationClick(flow)}
          >
            <img
              src={getApplicationIcon(source)}
              alt=""
              className={classes.tileLogo}
              decoding="async"
              draggable={false}
            />
            {/*
              A plain right arrow whichever way the integrations behind this
              pair happen to run: the chip stands for all of them, so a
              direction drawn here would be speaking for only some.
            */}
            <ArrowRightLong className={classes.arrow} />
            <img
              src={getApplicationIcon(target)}
              alt=""
              className={classes.tileLogo}
              decoding="async"
              draggable={false}
            />
          </button>
        );

        // Every copy is wrapped, the repeats included: a visitor hovers
        // whichever chip has drifted in front of them, and on all but the
        // narrowest window that is usually not the one in the first copy. The
        // repeats are inside an `aria-hidden` container, so the tip they carry
        // is never announced twice.
        return (
          <Tooltip
            key={flow.key}
            title={intl.formatMessage(
              {
                id: 'modules.login.prebuiltIntegrationFlows.flowTooltip',
                defaultMessage: '{source} → {target}',
              },
              { source, target }
            )}
            placement="top"
          >
            {chip}
          </Tooltip>
        );
      }),
    [
      flows,
      onIntegrationClick,
      intl,
      classes.tile,
      classes.tileLogo,
      classes.arrow,
    ]
  );

  const primaryTiles = useMemo(() => buildTiles(false), [buildTiles]);
  const duplicateTiles = useMemo(() => buildTiles(true), [buildTiles]);

  return (
    <Box
      className={clsx(
        classes.row,
        isStatic && classes.rowStatic,
        paused && classes.rowPaused
      )}
      style={
        {
          animationDuration: `${durationS}s`,
          [MARQUEE_SHIFT_VAR]: `${copyWidth}px`,
        } as CSSProperties
      }
      // The pause is the whole point of the observer above, and a generated
      // JSS class name is not something a test — or anyone debugging in the
      // inspector — can name.
      data-paused={paused ? 'true' : undefined}
    >
      {Array.from({ length: copies }, (unused, index) => (
        <Box
          // Copies are identical by construction, so the position is the
          // only identity there is.
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={index === 0 ? copyRef : undefined}
          className={classes.copy}
          aria-hidden={index === 0 ? undefined : 'true'}
        >
          {index === 0 ? primaryTiles : duplicateTiles}
        </Box>
      ))}
    </Box>
  );
}

// Memoised so a re-measure in the parent does not rebuild the row's chips.
const FlowRow = memo(FlowRowBase);

/**
 * A single row of application pairs drifting across the page, below the prompt
 * box on the login landing page. Each chip is a pair the published catalogue
 * has at least one prebuilt integration for.
 *
 * It replaces the connector marquee that used to sit here, and keeps its drift:
 * logos passing by answered "what can this thing talk to?" while the visitor
 * decided what to type, but led nowhere. A pair is something that can be
 * pressed, and pressing one carries the choice through sign in to the prebuilt
 * integrations catalogue, which opens on the integrations connecting those two
 * applications.
 */
function PrebuiltIntegrationFlows(props: PrebuiltIntegrationFlowsProps) {
  const { onIntegrationClick } = props;
  const classes = useStyles();
  const { flows, isLoading } = usePrebuiltIntegrationFlows();
  const rootRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  // Starts as visible: that is the state the row renders in anywhere the
  // observer below cannot run, and it avoids a paused first frame on the
  // common path where the strip is on screen from the start.
  const [isVisible, setIsVisible] = useState(true);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Measured here rather than in the row: this element is the visible strip,
  // sized by the page at `width: 100%`, so its width is independent of how
  // many copies the row puts inside it.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const measure = () => {
      const next = root.getBoundingClientRect().width;
      setContainerWidth((current) => (current === next ? current : next));
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(root);
    return () => observer.disconnect();
  }, [isLoading]);

  // ── On screen or not ──────────────────────────────────────────────────
  // The track is several windows wide and sits on its own compositor layer
  // for as long as the page lives. Scrolled out of the band there is nothing
  // to look at, so the animation and the layer are given back until it
  // returns.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    // Absent on older browsers; there the row keeps animating, which is
    // exactly the behaviour without this gate.
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      const { isIntersecting } = entry;
      setIsVisible((current) =>
        current === isIntersecting ? current : isIntersecting
      );
    });
    observer.observe(root);
    // Optional because the jsdom stub in `setupTests.ts` implements only
    // `observe` and `unobserve`; every real implementation has it.
    return () => observer.disconnect?.();
  }, [isLoading]);

  // Reserved rather than spinnered: this is decoration under the page's actual
  // call to action, and a spinner here would pull the eye off the prompt box.
  if (isLoading) {
    return <Box className={classes.placeholder} />;
  }

  // The catalogue is unreachable, or publishes nothing this row can draw.
  // Explore integrations above it is still the way in.
  if (flows.length === 0) return null;

  return (
    <Box
      ref={rootRef}
      className={clsx(
        classes.carouselRoot,
        prefersReducedMotion && classes.carouselRootStatic
      )}
      data-testid="prebuilt-integration-flows"
    >
      <FlowRow
        flows={flows}
        onIntegrationClick={onIntegrationClick}
        speedPxS={ROW_SPEED_PX_S}
        containerWidth={containerWidth}
        paused={!isVisible}
        isStatic={prefersReducedMotion}
      />
    </Box>
  );
}

// Login re-renders whenever the auth dialog opens or closes and whenever an
// entry point is pressed. Nothing here depends on any of that, and there is no
// reason to rebuild tens of chips for it. The guard only holds while the
// caller keeps `onIntegrationClick` stable, which Login does with `useCallback`.
export default memo(PrebuiltIntegrationFlows);
